import axios from 'axios';
import { v2Api, API_ROOT } from './v2Client';
import { ensureAccessToken } from './authTokens';
import { clubRequestHeaders } from '../currentClub';
import { normalizeMatchForScreen } from './matches.service';

/** Matchs du club (API ClubMatchController, docs/CLUB_MATCHES.md) et leurs types configurables. */

function normalizeCategory(c) {
  if (!c || c.id == null) return null;
  return {
    id: String(c.id),
    name: c.name || '',
    color: c.color || '#8E8E93',
    icon: c.icon || null,
    sort_order: Number(c.sort_order ?? 0),
    is_active: c.is_active !== false,
    // Ouvert aux joueurs (création d'une partie dans l'app) ; false = réservé à l'ERP.
    is_public: c.is_public !== false,
    is_default: c.is_default === true,
    match_count: Number(c.match_count ?? 0),
  };
}

function normalizePerson(p) {
  if (!p) return null;
  return { player_id: p.player_id ? String(p.player_id) : null, name: p.name || '', picture: p.picture || null, guest: Boolean(p.guest) };
}

function normalizeMatch(m) {
  if (!m || m.id == null) return null;
  return {
    id: String(m.id),
    sport_id: m.sport_id ? String(m.sport_id) : null,
    sport_code: m.sport_code || null,
    sport_name: m.sport_name || null,
    scoring_format_id: m.scoring_format_id ? String(m.scoring_format_id) : null,
    category: normalizeCategory(m.category),
    tournament_id: m.tournament_id ? String(m.tournament_id) : null,
    tournament_name: m.tournament_name || null,
    tournament_category_name: m.tournament_category_name || null,
    side_a: (m.side_a || []).map(normalizePerson).filter(Boolean),
    side_b: (m.side_b || []).map(normalizePerson).filter(Boolean),
    terrain_id: m.terrain_id ? String(m.terrain_id) : null,
    terrain_name: m.terrain_name || null,
    terrain_number: m.terrain_number ?? null,
    referee: normalizePerson(m.referee),
    scheduled_at: m.scheduled_at || null,
    status: m.status || null,
    running: Boolean(m.running),
    score_a: m.score_a ?? null,
    score_b: m.score_b ?? null,
    // Rencontre d'interclub du club (équipes libres) ; null hors rencontre.
    // Match importé (Importer un tournoi) : libellés du fichier, places à définir.
    match_import: normalizeMatchImport(m.match_import),
    encounter: m.encounter?.id
      ? {
          id: String(m.encounter.id),
          home_team_name: m.encounter.home_team_name || '',
          away_team_name: m.encounter.away_team_name || '',
          home_logo_url: m.encounter.home_logo_url || null,
          away_logo_url: m.encounter.away_logo_url || null,
          position: m.encounter.position ?? null,
        }
      : null,
  };
}

/** Match importé d'un fichier Tournament Planner (MatchImportInfoDto) ; null hors import. */
export function normalizeMatchImport(mi) {
  if (!mi?.id) return null;
  return {
    id: String(mi.id),
    name: mi.name || '',
    event_name: mi.event_name || '',
    round: mi.round || '',
    number: mi.number || '',
    pending_a: mi.pending_a || null,
    pending_b: mi.pending_b || null,
    // Liens du tableau (MatchDto seulement) : match où vont le vainqueur / le perdant, place 1 = A, 2 = B (« Voir bracket »).
    next_match_id: mi.next_match_id ? String(mi.next_match_id) : null,
    next_match_slot: mi.next_match_slot ?? null,
    loser_next_match_id: mi.loser_next_match_id ? String(mi.loser_next_match_id) : null,
    loser_next_match_slot: mi.loser_next_match_slot ?? null,
    // Catégorie et phase déduites du tableau par l'API (ImportPhases) : « GU15 - Group A » = GU15, « Poule A », pool.
    category: mi.category || null,
    category_name: mi.category_name || mi.category || null,
    phase: mi.phase || null,
    phase_type: mi.phase_type || null,
    phase_rank_start: mi.phase_rank_start ?? null,
    phase_rank_end: mi.phase_rank_end ?? null,
    phase_sort: mi.phase_sort ?? null,
    // Club et terrain du fichier (« Les Marmots », « MAR 1 ») ; autre club : `external_court` et `court_label`
    // (« Les Marmots · terrain 1 »). Créneau en double dans le fichier : `schedule_conflict`.
    location: mi.location || null,
    court: mi.court || null,
    external_court: Boolean(mi.external_court),
    court_label: mi.court_label || null,
    schedule_conflict: Boolean(mi.schedule_conflict),
  };
}

/** POST multipart `file` + `options` (JSON) pour l'aperçu / l'import d'un tournoi. */
async function postImport(path, file, options) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('options', JSON.stringify(options || {}));
  const access = await ensureAccessToken(API_ROOT);
  const headers = clubRequestHeaders();
  if (access) headers.Authorization = `Bearer ${access}`;
  const { data } = await axios.post(`${API_ROOT}${path}`, fd, {
    headers,
    maxContentLength: 50 * 1024 * 1024,
    maxBodyLength: 12 * 1024 * 1024,
  });
  return data;
}

function normalizeEncounter(e) {
  if (!e || e.id == null) return null;
  return {
    id: String(e.id),
    sport_id: e.sport_id ? String(e.sport_id) : null,
    scoring_format_id: e.scoring_format_id ? String(e.scoring_format_id) : null,
    home_team_name: e.home_team_name || '',
    away_team_name: e.away_team_name || '',
    home_logo_url: e.home_logo_url || null,
    away_logo_url: e.away_logo_url || null,
    terrain_id: e.terrain_id ? String(e.terrain_id) : null,
    terrain_name: e.terrain_name || null,
    scheduled_at: e.scheduled_at || null,
    matches: (e.matches || []).map(normalizeMatch).filter(Boolean),
    home_wins: Number(e.home_wins ?? 0),
    away_wins: Number(e.away_wins ?? 0),
  };
}

export const clubMatchService = {
  /** GET /ClubMatch/categories */
  async listCategories() {
    const { data } = await v2Api.get('/ClubMatch/categories');
    return (Array.isArray(data) ? data : []).map(normalizeCategory).filter(Boolean);
  },
  /** POST /ClubMatch/categories/upsert — sans id = création. */
  async upsertCategory(payload) {
    const { data } = await v2Api.post('/ClubMatch/categories/upsert', payload);
    return normalizeCategory(data);
  },
  /** POST /ClubMatch/categories/delete */
  async deleteCategory(id) {
    await v2Api.post('/ClubMatch/categories/delete', { id });
  },
  /** POST /ClubMatch/list — { q?, category_id?, status?: 'upcoming' | 'played' } */
  async list(filters = {}) {
    const { data } = await v2Api.post('/ClubMatch/list', filters);
    return (Array.isArray(data) ? data : []).map(normalizeMatch).filter(Boolean);
  },
  /** GET /ClubMatch/{id} — une ligne (même forme que list) ; null si introuvable (404 : match de box, partie ouverte). */
  async get(id) {
    try {
      const { data } = await v2Api.get(`/ClubMatch/${encodeURIComponent(String(id))}`);
      return normalizeMatch(data);
    } catch (e) {
      if (e?.response?.status === 404) return null;
      throw e;
    }
  },
  /**
   * POST /ClubMatch/grid — lots de la grille de l'écran Matchs (onglets Club / Tournoi), même forme que les saisons de
   * l'onglet Box : `{ id, label, rows }`, lignes normalisées comme `/Season/current_all` ; `box_name` = type du match
   * (club) ou tableau (tournoi), pour la colonne et le filtre correspondants.
   */
  // Fenêtre par défaut côté API : 60 derniers jours + tout l'à venir (+ sans date / en cours). `tournamentId` : seuls
  // les matchs de ce tournoi sont chargés (sans fenêtre), les autres tournois sont listés avec `matches_loaded: false`.
  // `options` : { from, to, days_back, all_dates, limit, offset } (voir ClubMatchGridRequest).
  async grid(origin = 'club', tournamentId = null, options = {}) {
    const { data } = await v2Api.post('/ClubMatch/grid', { ...options, origin, tournament_id: tournamentId });
    return (Array.isArray(data) ? data : []).map((b) => ({
      id: String(b.id),
      label: b.label || '',
      status: b.status || null,
      matches_loaded: b.matches_loaded !== false,
      has_more: b.has_more === true,
      // Tournoi importé (fichier Tournament Planner) : ses matchs sont des matchs du club.
      imported: b.imported === true,
      // Lot importé : tournoi créé par l'import (fiche tournoi de l'ERP) ; null avant le premier réimport.
      tournament_id: b.tournament_id ? String(b.tournament_id) : null,
      // Import ciblé : classement de ses poules (clé = tableau du fichier), même calcul que la qualification automatique.
      pool_standings: b.pool_standings && typeof b.pool_standings === 'object' ? b.pool_standings : null,
      // Match d'une rencontre du club : « Interclub » pour le filtre (tous les interclubs d'un coup, pas une valeur par
      // rencontre) ; la colonne affiche ses équipes (MatchesPage).
      rows: (b.matches || []).map((m) =>
        normalizeMatchForScreen({
          ...m,
          match_import: normalizeMatchImport(m.match_import),
          box_name: m.club_encounter?.id
            ? m.match_category_name || 'Interclub'
            : b.labels?.[m.id] || m.match_category_name || m.tournament_category_name || '—',
        }),
      ),
    }));
  },
  /** POST /ClubMatch/create — side_a / side_b : [{ player_id } | { guest_name }]. */
  async create(payload) {
    const { data } = await v2Api.post('/ClubMatch/create', payload);
    return normalizeMatch(data);
  },
  /** POST /ClubMatch/update — mêmes champs que create + id. */
  async update(id, payload) {
    const { data } = await v2Api.post('/ClubMatch/update', { ...payload, id });
    return normalizeMatch(data);
  },
  /** POST /ClubMatch/delete */
  async remove(id) {
    await v2Api.post('/ClubMatch/delete', { id });
  },
  /** POST /ClubMatch/encounters — rencontres d'interclub du club (équipes libres), avec leurs matchs. */
  async listEncounters(filters = {}) {
    const { data } = await v2Api.post('/ClubMatch/encounters', filters);
    return (Array.isArray(data) ? data : []).map(normalizeEncounter).filter(Boolean);
  },
  /** GET /ClubMatch/encounter/{id} ; null si introuvable. */
  async getEncounter(id) {
    try {
      const { data } = await v2Api.get(`/ClubMatch/encounter/${encodeURIComponent(String(id))}`);
      return normalizeEncounter(data);
    } catch (e) {
      if (e?.response?.status === 404) return null;
      throw e;
    }
  },
  /**
   * POST /ClubMatch/encounter_save — crée (sans id) ou modifie la rencontre et tous ses matchs en une fois :
   * { id?, sport_id, scoring_format_id?, home_team_name, away_team_name, home_logo_url?, away_logo_url?, terrain_id?, scheduled_at?, matches: [{ id?, side_a, side_b, scheduled_at?, terrain_id? }] }.
   */
  async saveEncounter(payload) {
    const { data } = await v2Api.post('/ClubMatch/encounter_save', payload);
    return normalizeEncounter(data);
  },
  /** GET /ClubMatch/encounter_teams — équipes déjà encodées (nom, dernier logo), les plus récentes d'abord. */
  async listEncounterTeams() {
    const { data } = await v2Api.get('/ClubMatch/encounter_teams');
    return (Array.isArray(data) ? data : [])
      .filter((x) => x?.name)
      .map((x) => ({ name: String(x.name), logo_url: x.logo_url || null }));
  },
  /** POST /ClubMatch/encounter_upload_logo (multipart) — logo d'équipe → URL publique (à envoyer dans home/away_logo_url). */
  async uploadEncounterLogo(file) {
    const fd = new FormData();
    fd.append('file', file);
    const access = await ensureAccessToken(API_ROOT);
    const headers = clubRequestHeaders();
    if (access) headers.Authorization = `Bearer ${access}`;
    const { data } = await axios.post(`${API_ROOT}/ClubMatch/encounter_upload_logo`, fd, {
      headers,
      maxContentLength: 6 * 1024 * 1024,
      maxBodyLength: 6 * 1024 * 1024,
    });
    return data?.url ?? null;
  },
  /** POST /ClubMatch/encounter_delete — la rencontre et tous ses matchs. */
  async removeEncounter(id) {
    await v2Api.post('/ClubMatch/encounter_delete', { id });
  },
  /** GET /ClubMatch/imports — tournois importés (fichier Tournament Planner), les plus récents d'abord. */
  async listImports() {
    const { data } = await v2Api.get('/ClubMatch/imports');
    return Array.isArray(data) ? data : [];
  },
  /**
   * POST /ClubMatch/import_preview (multipart) — aperçu, rien d'enregistré. `options` : { import_id?, name?, sport_id?,
   * scoring_format_id?, court_referees?: { "8": player_id | null }, player_choices?: { [name_key]: player_id | 'guest' },
   * delete_missing? }.
   */
  previewImport(file, options) {
    return postImport('/ClubMatch/import_preview', file, options);
  },
  /** POST /ClubMatch/import_apply (multipart) — mêmes champs : crée ou met à jour l'événement et ses matchs. */
  applyImport(file, options) {
    return postImport('/ClubMatch/import_apply', file, options);
  },
  /** POST /ClubMatch/import_delete — l'événement et tous ses matchs. */
  async removeImport(id) {
    // Des centaines de matchs (tournoi, équipe…) : bien au-delà des 15 s par défaut du client.
    await v2Api.post('/ClubMatch/import_delete', { id }, { timeout: 180000 });
  },
  /** POST /ClubMatch/import_structure — découpage d'un import (catégories, phases, liens, alertes, corrections). */
  async importStructure(id) {
    const { data } = await v2Api.post('/ClubMatch/import_structure', { id });
    return data;
  },
  /** POST /ClubMatch/import_court_referees — arbitres des terrains d'un import : `court` = ceux réglés sur l'import
   * ({ "101": { player_id, name } }), `terrain` = ceux de la Configuration pour les terrains du club. */
  async importCourtReferees(id) {
    const { data } = await v2Api.post('/ClubMatch/import_court_referees', { id });
    return { court: data?.court_referees || {}, terrain: data?.terrain_referees || {} };
  },
  /** POST /ClubMatch/import_court_referee — arbitre d'un terrain de l'import (club ou autre club « M1 »), sans réimport ;
   * posé sur les matchs pas encore lancés de ce terrain. `refereeId` null = aucun → { terrain_number, referee, updated_matches }. */
  async setImportCourtReferee(id, terrainNumber, refereeId) {
    const { data } = await v2Api.post('/ClubMatch/import_court_referee', { id, terrain_number: terrainNumber, referee_id: refereeId || null });
    return data;
  },
  /** POST /ClubMatch/import_configure — enregistre les corrections du découpage (`phase_config`, null = aucune). */
  async configureImport(id, phaseConfig) {
    const { data } = await v2Api.post('/ClubMatch/import_configure', { id, phase_config: phaseConfig || null });
    return data;
  },
};
